import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'

import { useUtil } from '@/composables/util'
import {
  ROLE_ADMIN,
  ROLE_STUDENT,
  ROLE_TEACHER,
  ROUTE_ADMIN,
  ROUTE_CUSTOMIZE,
  ROUTE_LEGACY,
  ROUTE_LOGIN,
  ROUTE_MY_RECORDINGS,
  ROUTE_PRACTICE,
  ROUTE_RECORDINGS,
  ROUTE_STUDENT_RECORDINGS,
  ROUTE_STUDENTS,
} from '@/constants/appConstants'
import store from '@/store'
import AboutView from '@/views/AboutView.vue'
import AdminView from '@/views/AdminView.vue'
import ContactView from '@/views/ContactView.vue'
import CustomizeView from '@/views/CustomizeView.vue'
import ForgotPassword from '@/views/ForgotPassword.vue'
import ImpersonateView from '@/views/ImpersonateView.vue'
import IndexView from '@/views/IndexView.vue'
import LegacyPracticeView from '@/views/LegacyPracticeView.vue'
import LoginView from '@/views/LoginView.vue'
import MyRecordingsView from '@/views/MyRecordingsView.vue'
import ParseView from '@/views/ParseView.vue'
import PracticeView from '@/views/PracticeView.vue'
import ProfileView from '@/views/ProfileView.vue'
import RecordingsView from '@/views/RecordingsView.vue'
import SetPassword from '@/views/SetPassword.vue'
import StudentDemo from '@/views/StudentDemo.vue'
import StudentRecordingsView from '@/views/StudentRecordingsView.vue'
import StudentsView from '@/views/StudentsView.vue'

const { GENDER_MAP, isProduction, isStaff, isStudent, isTeacher } = useUtil()

const routes = [
  {
    component: IndexView,
    meta: {
      public: true, // Allow access to even if not logged in
    },
    name: 'index',
    path: '/',
  },
  {
    component: StudentDemo,
    meta: {
      // Allow access to even if not logged in
      onlyWhenLoggedOut: true,
      public: true,
    },
    name: 'student-demo',
    path: '/student-demo',
  },
  {
    component: LoginView,
    meta: {
      // Allow access to even if not logged in
      onlyWhenLoggedOut: true,
      public: true,
    },
    name: 'login',
    path: '/login',
    props: (route: RouteLocationNormalized) => ({ ...route.params, ...route.query }),
  },
  {
    component: ParseView,
    meta: {
      public: true, // Allow access to even if not logged in
    },
    name: 'parse',
    path: '/parse',
  },
  {
    component: AboutView,
    meta: {
      public: true, // Allow access to even if not logged in
    },
    name: 'about',
    path: '/about',
  },
  {
    component: ContactView,
    meta: {
      public: true, // Allow access to even if not logged in
    },
    name: 'contact',
    path: '/contact',
  },
  {
    component: ForgotPassword,
    meta: {
      // Allow access to even if not logged in
      onlyWhenLoggedOut: true,
      public: true,
    },
    name: 'forgot-password',
    path: '/forgot-password',
    props: true,
  },
  {
    component: SetPassword,
    meta: {
      // Allow access to even if not logged in
      onlyWhenLoggedOut: true,
      public: true,
    },
    name: 'set-password',
    path: '/set-password/:secretToken',
    props: true,
  },

  // teacher pages
  {
    component: StudentsView,
    meta: { role: [ROLE_TEACHER] },
    name: 'students',
    path: ROUTE_STUDENTS,
  },
  {
    component: RecordingsView,
    meta: { role: [ROLE_TEACHER] },
    name: 'recordings',
    path: ROUTE_RECORDINGS,
  },
  {
    component: CustomizeView,
    meta: { role: [ROLE_TEACHER] },
    name: 'customize',
    path: ROUTE_CUSTOMIZE,
  },
  {
    component: StudentRecordingsView,
    meta: { role: [ROLE_TEACHER] },
    name: 'student-recordings',
    path: ROUTE_STUDENT_RECORDINGS,
  },

  // student pages
  {
    component: MyRecordingsView,
    meta: { role: [ROLE_STUDENT] },
    name: 'my-recordings',
    path: ROUTE_MY_RECORDINGS,
  },

  // common pages
  {
    component: PracticeView,
    meta: { role: [ROLE_STUDENT, ROLE_TEACHER] },
    name: 'practice',
    path: ROUTE_PRACTICE,
  },
  {
    component: LegacyPracticeView,
    meta: { role: [ROLE_STUDENT, ROLE_TEACHER] },
    name: 'legacy-practice',
    path: ROUTE_LEGACY,
  },

  {
    component: ProfileView,
    meta: { role: [ROLE_TEACHER, ROLE_STUDENT] },
    name: 'profile',
    path: '/profile',
    props: true,
  },
  {
    component: SetPassword,
    meta: { role: [ROLE_TEACHER, ROLE_STUDENT] },
    name: 'change-password',
    path: '/change-password',
  },
  {
    component: ImpersonateView,
    meta: { role: [ROLE_TEACHER, ROLE_STUDENT] },
    name: 'impersonate',
    path: '/impersonate',
  },

  // admin
  {
    component: AdminView,
    meta: { role: [ROLE_ADMIN] },
    name: 'admin',
    path: ROUTE_ADMIN,
  },

  // catch-all: redirect unknown paths to root
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

router.beforeEach((to, from, next) => {
  // debugger
  // console.log('beforeEach, to =')
  // console.log(to)
  // console.log('beforeEach, from =')
  // console.log(from)
  // console.log('beforeEach, next =')
  // console.log(next)
  const isPublic = to.matched.some((record) => record.meta.public)
  const onlyWhenLoggedOut = to.matched.some((record) => record.meta.onlyWhenLoggedOut)

  // only track history when not impersonating
  if (store && !store.getters.user.impersonator) {
    store.dispatch.user.setImpersonatorUrl(from.path)
    // console.log(`setImpersonatorUrl = ${store.getters.user.impersonatorUrl}`)
  }

  const hasExactRole = (record: { meta: Record<string, unknown> }, role: string) => {
    const roles = record.meta?.role as string[] | undefined
    return roles?.length === 1 && roles[0] === role
  }
  const studentOnlyPage = to.matched.some((record) => hasExactRole(record, ROLE_STUDENT))
  const teacherOnlyPage = to.matched.some((record) => hasExactRole(record, ROLE_TEACHER))
  const staffOnlyPage = to.matched.some((record) => hasExactRole(record, ROLE_ADMIN))

  const userIsLoggedIn = store.getters.user.userIsLoggedIn

  // this isn't a public page and the user isn't logged in
  if (!isPublic && !userIsLoggedIn) {
    // console.log('router.index 00')
    return next({
      path: ROUTE_LOGIN,
      query: { redirect: to.fullPath }, // Store the full path to redirect the user to after login
    })
  }

  // deal with gender redirects
  const realUser = store.getters.user.impersonator || store.getters.user.user
  if (realUser && realUser.properties && realUser.properties.gender) {
    const { gender } = realUser.properties
    const { hostname } = window.location
    // note that we need to use the impersonator's gender
    const genderHostname = GENDER_MAP[gender].host
    const newLocation = `${window.location.protocol}//${genderHostname}${
      window.location.port ? `:${window.location.port}` : ''
    }${window.location.pathname}?gx=x&redirect=${to.fullPath}` // indicate gender change

    if (hostname !== genderHostname) {
      // console.log(`router.index, hostname ${hostname} !== genderHostname ${genderHostname}`)
      const urlParams = new URLSearchParams(window.location.search)
      const gx = urlParams.get('gx')
      if (gx === 'x') {
        // we got here through a gender change redirect; don't redirect back
        // console.log('router.index detected gix param; avoiding gx flap')
        router.push(urlParams.get('redirect') as string)
      } else if (isProduction()) {
        if (store.getters.user.impersonator) {
          // console.log('router.index prevents impersonation across a gender change')
          return
        }
        // console.log(`router.index replace URL with ${newLocation}`)
        window.location.replace(newLocation)
      } else {
        // console.log(`production would route to ${newLocation}`)
      }
    }
  }

  // Do not allow user to visit login page or register page if they are logged in
  if (userIsLoggedIn) {
    // can only access this page when logged out
    if (onlyWhenLoggedOut) {
      // console.log('router.index A')
      return next('/')
    }

    if (isTeacher() && studentOnlyPage) {
      // console.log('router.index B')
      return next({ name: 'recordings' })
    }

    if (isStudent() && teacherOnlyPage) {
      // console.log('router.index C')
      return next({ name: 'practice' })
    }

    if (!isStaff() && staffOnlyPage) {
      // console.log('router.index D')
      return next({ name: '/' })
    }

    if (!!store.getters.user.impersonator && to.path === '/impersonate') {
      // console.log('router.index E')
      return next({ name: '/' })
    }
  }

  // console.log(`to.path = ${to.path}`)
  // console.log(`to.fullPath = ${to.fullPath}`)
  // console.log(`next = ${next}`)

  if (to.fullPath === '/') {
    if (userIsLoggedIn) {
      if (isTeacher()) {
        // console.log('router.index F')
        return next(ROUTE_RECORDINGS)
      }
      if (isStudent()) {
        // console.log('router.index G')
        return next(ROUTE_PRACTICE)
      }
    }
  }

  // console.log('router.index H')
  return next()
})

export default router
